import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

const animationDuration = 300;
const slideInLeft = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const slideOutLeft = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;

const slideInRight = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideOutRight = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

const SideContent = styled.div<{
  $direction: 'left' | 'right';
  $visible: boolean;
}>`
  position: absolute;
  top: 0;
  ${(props) => (props.$direction === 'left' ? 'left: 0;' : 'right: 0;')};
  width: 100%;
  height: 100%;
  transform-origin: ${(props) =>
      props.$direction === 'left' ? 'left' : 'right'}
    center;
`;

const SidePaneWrapper = styled.div<{
  $visible: boolean;
  $direction: 'left' | 'right';
}>`
  position: relative;
  height: 100%;
  overflow: hidden;

  width: ${({ $visible }) => ($visible ? '25%' : '0')};
  min-width: ${({ $visible }) => ($visible ? '280px' : '0')};
  transition:
    width ${animationDuration}ms cubic-bezier(0.29, 0.04, 0.23, 1),
    min-width ${animationDuration}ms cubic-bezier(0.29, 0.04, 0.23, 1);

  ${({ $visible, $direction }) => {
    if ($visible) {
      return css`
        ${SideContent} {
          animation: ${$direction === 'left' ? slideInLeft : slideInRight}
            ${animationDuration}ms forwards;
        }
      `;
    } else {
      return css`
        ${SideContent} {
          animation: ${$direction === 'left' ? slideOutLeft : slideOutRight}
            ${animationDuration}ms forwards;
        }
      `;
    }
  }};
`;

interface Props {
  visible: boolean;
  direction?: 'left' | 'right';
  className?: string;
}

export const SidePane = ({
  children,
  visible,
  direction = 'left',
  className,
}: PropsWithChildren<Props>) => {
  const [mounted, setMounted] = useState(visible);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    if (visible) {
      requestAnimationFrame(() => {
        setMounted(true);
      });
    } else if (mounted) {
      animationTimeoutRef.current = setTimeout(() => {
        setMounted(false);
      }, animationDuration);
    }

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [visible, mounted]);

  if (!mounted && !visible) {
    return null;
  }

  return (
    <SidePaneWrapper
      $visible={visible}
      $direction={direction}
      className={className}
    >
      <SideContent $direction={direction} $visible={visible}>
        {children}
      </SideContent>
    </SidePaneWrapper>
  );
};
